/* tslint:disable:only-arrow-functions */

import {
  ColumnConfiguration,
  RowConfiguration,
} from '@epicuro-next/ataraxia/grid-builder';
import { IDynamicItem } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ProductsOnTheWaySizesType } from 'models-recommendation';

import { IDynamicEventHandlerWithId } from '../../utils/eventhandler-from-action';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
} from '../events';
import { ProductsOnTheWayDataType } from '../tenants/common/products-on-the-way/products-on-the-way-helpers';

const onClickEventHandlers: IDynamicEventHandlerWithId[] = [
  openConfigStripEventHandler('parent', 'productData'),
  recommendationItemClickedEventHandler(
    {
      provider: 'id:root',
      property: 'panelName',
    },
    {
      provider: 'id:root',
      property: 'panelTrackingId',
    },
    {
      provider: 'parent',
      property: 'productData',
    },
  ),
];
const sumQuantitiesPropertyName = 'summedQuantities';
const quantityText: IDynamicItem = {
  id: 'products-on-the-way-quantity-text',
  if: [
    'EQ',
    [{ provider: 'id:root', property: 'showProductSizesPopover' }, true],
  ],
  contentTypeId: 'dynamic-elements-popover-wrapper',
  styles: {
    style: {
      color: 'black',
      bottom: '0px',
      // 2.5rem half the size of the internal content
      left: 'calc(50% - 2.5rem)',
      'font-size': '2rem',
    },
    classes: ['position-absolute', 'ml-1', 'cursor-pointer'],
  },
  data: {
    [sumQuantitiesPropertyName]: {
      provider: 'parent',
      property: 'productData',
      transform: function (productData: ProductsOnTheWayDataType): string {
        const quantitiesSummed = productData.sizes
          .map((s) => s.quantity)
          .reduce(
            (previousValue, currentValue) => previousValue + currentValue,
            0,
          );
        return quantitiesSummed.toString();
      }.toString(),
    },
    sizes: {
      provider: 'parent',
      property: 'productData',
      transform: function (
        productData: ProductsOnTheWayDataType,
      ): ProductsOnTheWaySizesType[] {
        return productData.sizes;
      }.toString(),
    },
  },
  inputs: {
    triggers: 'hover',
    triggerContent: {
      contentTypeId: 'dynamic-elements-circle-text',
      styles: {
        style: {
          margin: 'auto',
        },
        classes: ['cursor-pointer'],
      },
      inputs: {
        text: {
          provider: 'parent',
          property: sumQuantitiesPropertyName,
        },
      },
    },
    popoverContent: {
      contentTypeId: 'dynamic-elements-grid-builder',
      styles: {
        variables: {
          '--grid-builder-gap': '0px',
          '--grid-builder-header-bg': 'var(--color-brand-primary)',
          '--grid-builder-header-color': 'var(--color-brand-on-primary)',
          '--grid-builder-header-corner-bg':
            'var(--color-brand-hover-primary, #303f9f)',
        },
      },
      data: {
        sortedColumns: {
          provider: 'parent',
          property: 'sizes',
          transform: function (sizes: ProductsOnTheWaySizesType[]): string[] {
            return sizes
              .map((size) => size.size ?? '')
              .filter((value, index, array) => array.indexOf(value) === index)
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          }.toString(),
        },
        sortedRows: {
          provider: 'parent',
          property: 'sizes',
          transform: function (sizes: ProductsOnTheWaySizesType[]): string[] {
            return sizes
              .map((size) => size.width ?? '')
              .filter((value, index, array) => array.indexOf(value) === index);
          }.toString(),
        },
      },
      inputs: {
        gridCornerClass:
          'd-flex align-items-center font-weight-bold justify-content-center h3 mb-0',
        gridTitle: {
          provider: 'parent',
          property: sumQuantitiesPropertyName,
        },
        columns: {
          provider: 'self',
          property: 'sortedColumns',
          transform: function (sortedColumns: string[]): ColumnConfiguration[] {
            return sortedColumns.map((size) => ({
              columnName: size ?? '',
              bindProperty: size,
              columnHeaderClass: 'font-weight-bold px-1',
            }));
          }.toString(),
        },
        rows: {
          values: [
            {
              provider: 'parent',
              property: 'sizes',
            },
            {
              provider: 'self',
              property: 'sortedColumns',
            },
            {
              provider: 'self',
              property: 'sortedRows',
            },
          ],
          transform: function ([sizes, sortedColumns, sortedRows]: [
            ProductsOnTheWaySizesType[],
            string[],
            string[],
          ]) {
            const rows: RowConfiguration<{ [key: string]: number }>[] =
              sortedRows.map((width) => ({
                rowTitle: width ?? '',
                rowTitleClass: 'font-weight-bold mr-1',
                data: sortedColumns.reduce((acc, column) => {
                  acc[column] =
                    sizes.find((fromSizes) => {
                      return (
                        fromSizes.size === column &&
                        (fromSizes.width ? fromSizes.width === width : true)
                      );
                    })?.quantity ?? 0;
                  return acc;
                }, {} as { [key: string]: number }),
              }));
            return rows;
          }.toString(),
        },
        rowHeaderConfig: {
          rowHeaderWidth: '3rem',
        },
      },
    },
  },
  eventHandlers: {
    onClick: onClickEventHandlers,
  },
};

// tslint:disable-next-line:variable-name
export const ProductSizesPopover = quantityText;
